/**
 * FileDropPaste - エディタへの画像・PDF のドラッグ＆ドロップ / 貼り付けを受け付ける拡張。
 *
 * ファイルごとにアップロード用ノード (画像: imageUpload / PDF: pdfEmbed) を挿入し、
 * ファイル本体は pendingFileId 経由でノードビューに渡してそこでアップロードさせる。
 */

import { Extension } from '@tiptap/core'
import type { Editor, JSONContent } from '@tiptap/core'
import { Plugin, PluginKey } from '@tiptap/pm/state'
import { toast } from 'sonner'

import { MAX_FILE_SIZE } from '@/lib/tiptap-utils'

const pendingFiles = new Map<string, File>()

/** ノードビューがアップロードするファイルを預け、取り出し用の ID を返す */
const registerPendingFile = (file: File): string => {
  const id = crypto.randomUUID()
  pendingFiles.set(id, file)
  return id
}

/** 預けたファイルを取り出す (一度きり) */
export const takePendingFile = (id: string | null | undefined): File | undefined => {
  if (!id) return undefined
  const file = pendingFiles.get(id)
  pendingFiles.delete(id)
  return file
}

/** ノード属性として JSON に載せるだけで HTML には出さない */
export const pendingFileIdAttribute = {
  default: null as string | null,
  parseHTML: () => null,
  renderHTML: () => ({}),
}

export const isPdfFile = (file: File): boolean =>
  file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf')

const isImageFile = (file: File): boolean => file.type.startsWith('image/')

const toUploadNode = (file: File): JSONContent | null => {
  if (isPdfFile(file)) {
    return {
      type: 'pdfEmbed',
      attrs: { name: file.name, size: file.size, pendingFileId: registerPendingFile(file) },
    }
  }
  if (!isImageFile(file)) return null
  if (file.size > MAX_FILE_SIZE) {
    toast.error(`${file.name}: 画像のサイズは ${MAX_FILE_SIZE / (1024 * 1024)}MB までです`)
    return null
  }
  return { type: 'imageUpload', attrs: { pendingFileId: registerPendingFile(file) } }
}

const buildUploadNodes = (files: File[]): JSONContent[] =>
  files.map(toUploadNode).filter((node): node is JSONContent => node !== null)

const hasSupportedFile = (files: File[]): boolean => files.some((f) => isPdfFile(f) || isImageFile(f))

const insertFiles = (editor: Editor, files: File[], pos?: number): boolean => {
  if (!editor.isEditable || !hasSupportedFile(files)) return false
  const nodes = buildUploadNodes(files)
  if (nodes.length === 0) return true
  const chain = editor.chain().focus()
  if (pos === undefined) chain.insertContent(nodes).run()
  else chain.insertContentAt(pos, nodes).run()
  return true
}

type ClipboardLike = { files: ArrayLike<File>; getData: (format: string) => string }

/** 貼り付けられたファイルを挿入する。処理したら true */
export const pasteFiles = (editor: Editor, data: ClipboardLike | null | undefined): boolean => {
  const files = Array.from(data?.files ?? [])
  if (files.length === 0) return false
  // Excel などはセルの画像も一緒に載せてくるので、テキストがあればテキストとして貼る
  if (data?.getData('text/plain').trim()) return false
  return insertFiles(editor, files)
}

export const FileDropPaste = Extension.create({
  name: 'fileDropPaste',

  addProseMirrorPlugins() {
    const editor = this.editor
    return [
      new Plugin({
        key: new PluginKey('fileDropPaste'),
        props: {
          handlePaste: (_view, event) => pasteFiles(editor, event.clipboardData),
          handleDrop: (view, event, _slice, moved) => {
            if (moved) return false
            const files = Array.from(event.dataTransfer?.files ?? [])
            if (files.length === 0) return false
            const coords = view.posAtCoords({ left: event.clientX, top: event.clientY })
            return insertFiles(editor, files, coords?.pos)
          },
        },
      }),
    ]
  },
})
